<template>
    <proofreading-category-accordion ref="accordionRef" :label="label" :default-open="defaultOpen"
        scroll-to-top-on-open>
        <div v-for="(issue, index) in issues" :key="issue.id" class="proofreading-category-accordion__item">
            <issue-item :issue="issue" :is-active="isIssueActive(issue)" :active-location-ids="activeIssueIds"
                :category-label="issue.issueTypeLabel" :default-open="index === 1" @click="onClickIssue" @apply="onApplyIssue"
                @revert="onRevertIssue" @reject-toggle="onRejectToggleIssue" @hover="onHoverIssue" />
        </div>
    </proofreading-category-accordion>
</template>
<script lang="ts" setup>
import { defineEmits, defineProps, nextTick, ref, watch } from 'vue';
import type { ProofreadingIssue } from '@/types/proofreading';
import ProofreadingCategoryAccordion from '@/components/organisms/proofreading/shared/ProofreadingCategoryAccordion.vue';
import IssueItem from './IssueItem.vue';

const props = defineProps<{
    categoryId: string;
    label: string;
    issues: ProofreadingIssue[];
    activeIssueIds: string[];
    defaultOpen?: boolean;
}>();

const emit = defineEmits<{
    (event: 'issue-click', payload: { issueId: string; episodeKey: string }): void;
    (event: 'issue-apply', payload: { categoryId: string; issue: ProofreadingIssue }): void;
    (event: 'issue-revert', payload: { categoryId: string; issue: ProofreadingIssue }): void;
    (event: 'issue-reject-toggle', payload: { categoryId: string; issue: ProofreadingIssue }): void;
    (event: 'issue-hover', issue: ProofreadingIssue, isHovered: boolean): void;
}>();

const accordionRef = ref<InstanceType<typeof ProofreadingCategoryAccordion> | null>(null);

function openAccordionIfGroupHasActiveIssue(issueIds: string[]) {
    if (issueIds.length === 0) return;
    const isInGroup = props.issues.some(
        (issue) =>
            issueIds.includes(issue.id)
            || (issue.locations?.some((loc) => issueIds.includes(loc.id)) ?? false),
    );
    if (isInGroup) {
        accordionRef.value?.open();
    }
}

watch(
    () => props.activeIssueIds,
    (issueIds) => {
        nextTick(() => {
            openAccordionIfGroupHasActiveIssue(issueIds);
        });
    },
    { deep: true },
);

function isIssueActive(issue: ProofreadingIssue): boolean {
    // target 未設定（初期状態）ではすべてフル opacity にする
    if (props.activeIssueIds.length === 0) return true;
    return props.activeIssueIds.some(
        (activeId) =>
            issue.id === activeId || (issue.locations?.some((loc) => loc.id === activeId) ?? false),
    );
}

function onClickIssue(payload: { issueId: string; episodeKey: string }) {
    emit('issue-click', payload);
}

function onApplyIssue(issue: ProofreadingIssue) {
    emit('issue-apply', { categoryId: props.categoryId, issue });
}

function onRevertIssue(issue: ProofreadingIssue) {
    emit('issue-revert', { categoryId: props.categoryId, issue });
}

function onRejectToggleIssue(issue: ProofreadingIssue) {
    emit('issue-reject-toggle', { categoryId: props.categoryId, issue });
}

function onHoverIssue(issue: ProofreadingIssue, isHovered: boolean) {
    emit('issue-hover', issue, isHovered);
}
</script>
<style lang="scss" scoped>
@import "@/style-common.scss";

.proofreading-category-accordion__item {
    margin-top: 12px;
}
</style>
